import type { ReactNode } from 'react';
import { I18nProvider as SharedProvider, useMessages, type Locale } from '@bounded/ui/i18n';
import { uiEn, type UiMessages } from '@bounded/ui/messages/en';
import { en, type ConsoleMessages } from './en';

/*
 * Console messages on top of the shared UI messages (@bounded/ui/i18n has the conventions).
 * What is translated: interface copy, and server content that ships translations (preset
 * metadata, policy parameter labels via `i18n[locale]`). What is never translated: anything a
 * device wrote — it is shown exactly as received.
 */
export { LOCALES, detectLocale, pickLocalized, useFormat, useLocale, type Locale } from '@bounded/ui/i18n';
export type Messages = UiMessages & ConsoleMessages;

/** Console-only API error codes join the shared ones so ErrorNotice explains them like any other. */
const merge = (ui: UiMessages, app: ConsoleMessages): Messages => ({ ...ui, ...app, errors: { ...ui.errors, codes: { ...ui.errors.codes, ...app.errorCodes } } });
const loaders: Record<Locale, () => Promise<Messages>> = {
  en: async () => merge(uiEn, en),
  'zh-CN': async () => {
    const [{ uiZhCN }, { zhCN }] = await Promise.all([import('@bounded/ui/messages/zh-CN'), import('./zh-CN')]);
    return merge(uiZhCN, zhCN);
  },
};
export const loadMessages = (locale: Locale) => loaders[locale]();

export function I18nProvider({ initial, messages, children }: { initial: Locale; messages: Messages; children: ReactNode }) {
  return <SharedProvider initial={initial} messages={messages} load={loadMessages}>{children}</SharedProvider>;
}
export const useT = () => useMessages<Messages>();
